/**
 * iter-296: Helper components for the Storage Estimator's per-estimate
 * Hardware Brands / Electrical / Additional Hardware panels.
 *
 * BrandRateRow — brand <Select> + auto-fill rate + free-text custom brand
 * QtyRateGrid  — compact qty + rate grid for electrical / extras line items
 */
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { HARDWARE_BRANDS } from '@/components/estimator/storageConfig';

export function BrandRateRow({
  label, category, brand, onBrandChange,
  customLabel, onCustomLabelChange,
  rate, onRateChange, rateOnBrandChange,
  testId,
}) {
  const list = HARDWARE_BRANDS[category] || [];
  const selected = list.find(b => b.id === brand);
  const isCustom = !!selected?.isCustom;

  const handleBrandChange = (id) => {
    onBrandChange(id);
    const opt = list.find(b => b.id === id);
    if (opt && !opt.isCustom && rateOnBrandChange) {
      rateOnBrandChange(opt.rate);
    }
  };

  return (
    <div className="space-y-1.5" data-testid={`${testId}-row`}>
      <Label className="text-xs text-stone-700 font-medium">{label}</Label>
      <div className="flex items-center gap-2">
        <Select value={brand} onValueChange={handleBrandChange}>
          <SelectTrigger className="h-8 text-xs flex-1" data-testid={`${testId}-brand-select`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {list.map(b => (
              <SelectItem key={b.id} value={b.id}>
                {b.label}{!b.isCustom && b.rate > 0 ? ` — Rs ${b.rate}` : ''}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Input
          type="number"
          value={rate}
          onChange={(e) => onRateChange(+e.target.value)}
          className="h-8 text-xs w-24"
          data-testid={`${testId}-rate-input`}
          placeholder="Rs"
        />
      </div>
      {isCustom && (
        <Input
          type="text"
          value={customLabel}
          onChange={(e) => onCustomLabelChange(e.target.value)}
          placeholder="Custom brand name"
          className="h-7 text-xs"
          data-testid={`${testId}-custom-label-input`}
        />
      )}
    </div>
  );
}

export function QtyRateGrid({ lines, onChange, testIdPrefix }) {
  const update = (id, patch) => onChange(lines.map(l => (l.id === id ? { ...l, ...patch } : l)));
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2">
      {lines.map(l => {
        const lineTotal = (+l.qty || 0) * (+l.rate || 0);
        return (
          <div key={l.id} className="flex items-center gap-2 text-xs" data-testid={`${testIdPrefix}-${l.id}-row`}>
            <Label className="flex-1 text-stone-700 truncate" title={l.label}>
              {l.label}
              {l.unit && <span className="text-stone-400 text-[10px] ml-1">({l.unit})</span>}
            </Label>
            <Input
              type="number"
              value={l.qty}
              onChange={(e) => update(l.id, { qty: +e.target.value })}
              className="h-7 w-16 text-xs"
              placeholder="qty"
              data-testid={`${testIdPrefix}-${l.id}-qty`}
            />
            <Input
              type="number"
              value={l.rate}
              onChange={(e) => update(l.id, { rate: +e.target.value })}
              className="h-7 w-20 text-xs"
              placeholder="rate"
              data-testid={`${testIdPrefix}-${l.id}-rate`}
            />
            <span className={`text-[11px] font-mono w-20 text-right ${lineTotal > 0 ? 'text-emerald-700 font-semibold' : 'text-stone-300'}`}>
              {lineTotal > 0 ? `Rs ${lineTotal.toLocaleString()}` : '—'}
            </span>
          </div>
        );
      })}
    </div>
  );
}
